import React, { FC, Fragment, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
    Alert,
    Box,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import OrganizationPicker from 'components/orders/organization-picker.component';
import { formatDate } from 'components/orders/order-format';
import {
    INVOICE_PAYMENT_STATUSES,
    INVOICE_STATUSES,
    INVOICE_TYPES,
    formatTotal,
    invoiceLabel,
} from 'components/invoices/invoice-format';
import { adminInvoicesTemplate } from 'apollo/templates/invoices';
import { AdminInvoiceFilters, AdminInvoicePage } from 'models/admin-invoice';
import { AdminOrderOrganization } from 'models/admin-order';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Filters live in the URL so a filtered list can be shared and linked to (lab and clinic rows link here). */
const useInvoiceListParams = () => {
    const [params, setParams] = useSearchParams();
    const organization = (key: string): AdminOrderOrganization | null =>
        params.get(key) ? { _id: params.get(key) as string, name: params.get(`${key}Name`) ?? undefined } : null;

    const update = (changes: Record<string, string | null | undefined>, resetPage = true) => {
        const next = new URLSearchParams(params);
        Object.entries(changes).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
        if (resetPage) next.delete('page');
        setParams(next, { replace: true });
    };

    return {
        search: params.get('search') ?? '',
        lab: organization('lab'),
        customer: organization('customer'),
        type: params.get('type') ?? '',
        status: params.get('status') ?? '',
        paymentStatus: params.get('payment') ?? '',
        page: Number(params.get('page') ?? 0),
        pageSize: Number(params.get('size') ?? PAGE_SIZE_OPTIONS[0]),
        update,
    };
};

type FilterSelectProps = {
    label: string;
    value: string;
    options: string[];
    group: string;
    onChange: (value: string | null) => void;
    dataTest: string;
};

const FilterSelect: FC<FilterSelectProps> = ({ label, value, options, group, onChange, dataTest }) => {
    const { t: translate } = useTranslation('super-admin');
    return (
        <FormControl fullWidth size="small">
            <InputLabel>{label}</InputLabel>
            <Select
                variant="outlined"
                label={label}
                value={value}
                onChange={(event) => onChange(event.target.value || null)}
                data-test={dataTest}
            >
                <MenuItem value="">{translate('super-admin:ALL')}</MenuItem>
                {options.map((option) => (
                    <MenuItem key={option} value={option}>
                        {invoiceLabel(translate, group, option)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const Invoices: FC = () => {
    const { t: translate } = useTranslation('super-admin');
    const navigate = useNavigate();
    const { search, lab, customer, type, status, paymentStatus, page, pageSize, update } = useInvoiceListParams();
    const [searchInput, setSearchInput] = useState(search);
    const [debouncedSetSearch] = useState(() =>
        debounce((text: string, apply: typeof update) => apply({ search: text.trim() || null }), 400),
    );

    useEffect(() => {
        if (searchInput !== search) debouncedSetSearch(searchInput, update);
    }, [searchInput]);

    const filters: AdminInvoiceFilters = {
        search: search || undefined,
        labId: lab?._id,
        customerId: customer?._id,
        type: type || undefined,
        status: status || undefined,
        paymentStatus: paymentStatus || undefined,
    };

    const { data, loading, error } = useQuery<{ adminInvoices: AdminInvoicePage }>(adminInvoicesTemplate, {
        variables: { filters, limit: pageSize, offset: page * pageSize },
        fetchPolicy: 'network-only',
    });
    const result = data?.adminInvoices;

    const setOrganization = (key: 'lab' | 'customer', organization: AdminOrderOrganization | null) =>
        update({ [key]: organization?._id, [`${key}Name`]: organization?.name });

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('super-admin:MENU_INVOICES')} />
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={translate('super-admin:INVOICE_SEARCH')}
                        helperText={translate('super-admin:INVOICE_SEARCH_HELP')}
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        data-test="invoices.search"
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <OrganizationPicker
                        label={translate('super-admin:INVOICE_LAB')}
                        value={lab}
                        type="lab"
                        onChange={(organization) => setOrganization('lab', organization)}
                        dataTest="invoices.lab"
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <OrganizationPicker
                        label={translate('super-admin:INVOICE_CUSTOMER')}
                        value={customer}
                        onChange={(organization) => setOrganization('customer', organization)}
                        dataTest="invoices.customer"
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={1}>
                    <FilterSelect
                        label={translate('super-admin:INVOICE_TYPE')}
                        value={type}
                        options={INVOICE_TYPES}
                        group="TYPE"
                        onChange={(value) => update({ type: value })}
                        dataTest="invoices.type"
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                    <FilterSelect
                        label={translate('super-admin:INVOICE_STATUS')}
                        value={status}
                        options={INVOICE_STATUSES}
                        group="STATUS"
                        onChange={(value) => update({ status: value })}
                        dataTest="invoices.status"
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={1}>
                    <FilterSelect
                        label={translate('super-admin:INVOICE_PAYMENT_STATUS')}
                        value={paymentStatus}
                        options={INVOICE_PAYMENT_STATUSES}
                        group="PAYMENT"
                        onChange={(value) => update({ payment: value })}
                        dataTest="invoices.payment-status"
                    />
                </Grid>
            </Grid>

            {error ? <Alert severity="error">{error.message}</Alert> : undefined}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('super-admin:INVOICE_NUMBER')}</TableCell>
                            <TableCell>{translate('super-admin:INVOICE_DATE')}</TableCell>
                            <TableCell>{translate('super-admin:INVOICE_LAB')}</TableCell>
                            <TableCell>{translate('super-admin:INVOICE_CUSTOMER')}</TableCell>
                            <TableCell align="right">{translate('super-admin:INVOICE_ORDERS')}</TableCell>
                            <TableCell align="right">{translate('super-admin:INVOICE_TOTAL')}</TableCell>
                            <TableCell>{translate('super-admin:INVOICE_STATUS')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Loader />
                                </TableCell>
                            </TableRow>
                        ) : undefined}
                        {!loading && result && result.items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('super-admin:INVOICE_NO_RESULTS')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : undefined}
                        {!loading &&
                            result?.items.map((invoice) => (
                                <TableRow
                                    key={invoice._id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/invoices/${invoice._id}`)}
                                    data-test="invoices.row"
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                            {invoice.invoiceNumber ?? '–'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {invoiceLabel(translate, 'KIND', invoice.invoiceType)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(invoice.invoiceDate)}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {translate('super-admin:INVOICE_DUE')} {formatDate(invoice.dueDate)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{invoice.lab?.name ?? '–'}</TableCell>
                                    <TableCell>{invoice.customer?.name ?? '–'}</TableCell>
                                    <TableCell align="right">{invoice.orderCount}</TableCell>
                                    <TableCell align="right">{formatTotal(invoice.total)}</TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            <Chip
                                                size="small"
                                                label={invoiceLabel(translate, 'STATUS', invoice.status)}
                                            />
                                            {invoice.type === 'CREDIT' ? (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={invoiceLabel(translate, 'TYPE', invoice.type)}
                                                />
                                            ) : undefined}
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={invoiceLabel(translate, 'PAYMENT', invoice.paymentStatus)}
                                            />
                                        </Box>
                                        {invoice.sentOn ? (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {translate('super-admin:INVOICE_SENT_ON', {
                                                    date: formatDate(invoice.sentOn),
                                                    method: invoiceLabel(translate, 'DELIVERY', invoice.deliveryMethod),
                                                })}
                                            </Typography>
                                        ) : undefined}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {result ? (
                <TablePagination
                    component="div"
                    count={result.total}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={(_, nextPage) => update({ page: String(nextPage) }, false)}
                    onRowsPerPageChange={(event) => update({ size: event.target.value })}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} ${translate('super-admin:OF')} ${result.totalIsEstimate ? '~' : ''}${count}`
                    }
                />
            ) : undefined}
        </Fragment>
    );
};

export default Invoices;
